import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type AdminUser = components['schemas']['AdminUser'];
export type AdminUserPage = components['schemas']['AdminUserPage'];
export type Role = components['schemas']['Role'];
export type UserCreate = components['schemas']['UserCreate'];
export type UserUpdate = components['schemas']['UserUpdate'];
export type ExternalIdType = components['schemas']['ExternalIdType'];

export interface AdminUserQuery {
  q?: string | undefined;
  role?: Role | undefined;
  page: number;
  pageSize: number;
}

/** A page of users by name; keeps the last page on screen while the next loads. */
export function useAdminUsers({ q, role, page, pageSize }: AdminUserQuery) {
  const api = useApiClient();
  const query = { ...(q ? { q } : {}), ...(role ? { role } : {}), page, pageSize };
  return useQuery({
    queryKey: queryKeys.adminUserPage(query),
    queryFn: () => unwrap(api.GET('/api/admin/users', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

/** Everyone who holds the supervisor role, by name: the Supervisors picker's options. */
export function useSupervisors(enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.supervisors(),
    queryFn: async () => {
      const users: AdminUser[] = [];
      for (let page = 1; ; page += 1) {
        const result = await unwrap(
          api.GET('/api/admin/users', {
            params: { query: { role: 'supervisor', page, pageSize: 100 } },
          }),
        );
        users.push(...result.items);
        if (users.length >= result.total || result.items.length === 0) return users;
      }
    },
    enabled,
  });
}

export function useExternalIdTypes(enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.externalIdTypes(),
    queryFn: async () => (await unwrap(api.GET('/api/admin/external-id-types'))).items,
    staleTime: Infinity,
    enabled,
  });
}

/** Creates a user, then refreshes every user list. */
export function useCreateUser() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreate) => unwrap(api.POST('/api/admin/users', { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers() }),
  });
}

/** Changes a user (including enabling or disabling), then refreshes every user list. */
export function useUpdateUser() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UserUpdate }) =>
      unwrap(api.PATCH('/api/admin/users/{user_id}', { params: { path: { user_id: id } }, body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers() }),
  });
}
